import * as React from 'react';
import { cva } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const variantStyles = {
  default: {
    '--color-primary-dark': '#313fa9',
    '--color-primary-lighter': '#DDF4FF'
  },
  turbo: {
    '--color-primary-dark': '#df274c',
    '--color-primary': '#FF4C6F',
    '--color-primary-light': '#FDE5EA',
    '--color-primary-lighter': '#FFF6F8'
  },
  conquest: {
    '--color-primary-dark': '#ef9526',
    '--color-primary': '#F9AD53',
    '--color-primary-light': '#FDB462',
    '--color-primary-lighter': '#FFF5E9'
  }
};

const challengeCardVariants = cva('relative flex flex-col rounded-xl border border-solid border-neutral-lightest bg-white transition-all duration-150 ease-in-out shadow-dropshadow p-6 pt-9 hover:shadow-dropshadow-md', {
  variants: {
    done: {
      true: 'pointer-events-none'
    }
  },
  defaultVariants: {
    variant: 'default',
    done: false
  }
});

const ChallengeCard = React.forwardRef(({ className, variant = 'default', done, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('c-challenge-card', challengeCardVariants({ variant, done }), className)}
    style={variantStyles[variant]}
    {...props}
  />
));
ChallengeCard.displayName = 'ChallengeCard';

const ChallengeCardMedalDone = React.forwardRef(({ show, className, ...props }, ref) =>
  show ? (
    <img
      ref={ref}
      className={cn('c-challenge-card-medal', 'absolute right-0 top-0 w-9 -translate-y-[40%] translate-x-[40%]', className)}
      {...props}
    />
  ) : null
);
ChallengeCardMedalDone.displayName = 'ChallengeCardMedalDone';

const ChallengeCardHeader = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('c-challenge-card-header', 'mb-6', className)}
    {...props}
  />
));
ChallengeCardHeader.displayName = 'ChallengeCardHeader';

const ChallengeCardHat = React.forwardRef(({ className, ...props }, ref) => (
  <span
    ref={ref}
    className={cn(
      'c-challenge-card-hat',
      'absolute left-1/2 top-0 inline-block max-w-[80%] -translate-x-1/2 truncate rounded-b-md border-2 border-t-0 border-solid border-white bg-primary-lighter px-2 py-0.5 text-[10px] font-bold uppercase text-primary shadow-dropshadow',
      className
    )}
    {...props}
  />
));
ChallengeCardHat.displayName = 'ChallengeCardHat';

const ChallengeCardBody = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('c-challenge-card-body', 'mb-4 flex flex-grow flex-col justify-between break-words', className)}
    {...props}
  />
));
ChallengeCardBody.displayName = 'ChallengeCardBody';

const ChallengeCardText = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('c-challenge-card-text', 'mb-6 text-[10px] font-light text-neutral-dark [&_p]:mb-0 [&_p]:w-full [&_p]:truncate', className)}
    {...props}
  />
));
ChallengeCardText.displayName = 'ChallengeCardText';

const ChallengeCardTitle = React.forwardRef(({ className, ...props }, ref) => (
  <h3
    ref={ref}
    className={cn('c-challenge-card-title', 'mb-0 w-full truncate text-sm font-bold text-primary-dark', className)}
    {...props}
  />
));
ChallengeCardTitle.displayName = 'ChallengeCardTitle';

const ChallengeCardName = React.forwardRef(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn('c-challenge-card-name', 'w-full truncate text-xs font-bold text-neutral-dark', className)}
    {...props}
  />
));
ChallengeCardName.displayName = 'ChallengeCardName';

const ChallengeCardCategory = React.forwardRef(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn('c-challenge-card-category', 'mb-2 flex items-center gap-2 text-xs font-light text-primary-dark', className)}
    {...props}
  />
));
ChallengeCardCategory.displayName = 'ChallengeCardCategory';

const ChallengeCardIcon = React.forwardRef(({ className, square, ...props }, ref) => (
  <span
    ref={ref}
    className={cn('c-challenge-card-icon', 'flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-primary-lighter [&_svg]:w-3.5 [&_svg]:stroke-primary', square && 'rounded-sm', className)}
    {...props}
  />
));
ChallengeCardIcon.displayName = 'ChallengeCardIcon';

const ChallengeCardBanner = React.forwardRef(({ className, variant, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-challenge-card-banner',
      '-mx-6 flex h-[47px] items-center gap-3 bg-primary-lighter px-4 text-sm font-bold uppercase text-primary transition-all duration-300',
      variant === 'conquest' && 'justify-center [&_.challenge-card-banner-text]:w-auto [&_.challenge-card-banner-text]:grow-0',
      className
    )}
    {...props}
  />
));
ChallengeCardBanner.displayName = 'ChallengeCardBanner';

const ChallengeCardBannerIcon = React.forwardRef(({ className, ...props }, ref) => (
  <span
    ref={ref}
    className={cn('c-challenge-card-banner-icon', '[&_rect]:fill-primary-light [&_svg]:w-6 [&_svg]:flex-shrink-0 [&_svg]:transition-all [&_svg]:duration-300', className)}
    {...props}
  />
));
ChallengeCardBannerIcon.displayName = 'ChallengeCardBannerIcon';

const ChallengeCardBannerText = React.forwardRef(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn('c-challenge-card-banner-text', 'challenge-card-banner-text mb-0 w-0 grow truncate text-center', className)}
    {...props}
  />
));
ChallengeCardBannerText.displayName = 'ChallengeCardBannerText';

const ChallengeCardFooter = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('c-challenge-card-footer', 'flex h-10 items-center justify-center', className)}
    {...props}
  />
));
ChallengeCardFooter.displayName = 'ChallengeCardFooter';

export {
  ChallengeCard,
  ChallengeCardMedalDone,
  ChallengeCardHeader,
  ChallengeCardHat,
  ChallengeCardBody,
  ChallengeCardText,
  ChallengeCardTitle,
  ChallengeCardName,
  ChallengeCardCategory,
  ChallengeCardIcon,
  ChallengeCardBanner,
  ChallengeCardBannerIcon,
  ChallengeCardBannerText,
  ChallengeCardFooter
};
